import { BottomSheetDottedBackground } from '../../components/BottomSheet/BottomSheetDottedBackground';
import { ProgressBar } from '../../components/ProgressBar';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { useAnnounceChange } from '../../utils/useAnnounceChange';
import { PROCESSING_STAGE_LAYOUT as L } from './processing-stage.layout';
import { ProcessingProTip } from './ProcessingProTip';
import type { ProcessingStageProps } from './ProcessingStage.types';

const ROOT_STYLE = { paddingHorizontal: L.paddingX, paddingVertical: L.paddingY };
const ROOT_WEB_STYLE = { padding: `${L.paddingY}px ${L.paddingX}px` };
const COLUMN_STYLE = { width: '100%', maxWidth: L.maxWidth, gap: L.gap } as const;
const COLUMN_WEB_STYLE = { width: '100%', maxWidth: L.maxWidth, gap: L.gap };
const KA_STYLE = { width: L.kaSize, height: L.kaSize };
const TITLE_STYLE = { gap: L.titleGap };

/**
 * The upload & processing screen — ONE treatment for every wait on a file
 * the user handed us (uploading, reading pages, transcribing, importing,
 * creating the deck), on both clients, in ONE fixed order and geometry
 * ({@link PROCESSING_STAGE_LAYOUT}) no caller can change:
 *
 *   Prof. Ka · title · (subtitle) · bar · pro tip · actions (Cancel last).
 *
 * The bar never carries a percent, a page count or an ETA, and never creeps
 * on a timer: no real fraction = the indeterminate bar. A subtitle replaces
 * the pro tip. Labels arrive pre-translated (the DS carries no i18n) — the
 * `processing.*` shared bundle.
 */
export function ProcessingStage({
  renderKa,
  kaStage,
  title,
  detail,
  progress,
  indeterminate = false,
  actions,
  proTip,
  dotted = false,
  progressAccessibilityLabel,
  className,
  testID,
}: ProcessingStageProps) {
  // iOS reads no live regions: say the new title when the wait moves on.
  useAnnounceChange(title);
  return (
    <View
      testID={testID}
      className={cn(
        'relative w-full flex-1 items-center justify-center',
        dotted && 'bg-background',
        className,
      )}
      nativeProps={{ style: ROOT_STYLE, accessibilityState: { busy: true } }}
      // No `aria-busy` on web: screen readers hold back a busy region's live
      // updates, so the status below ("Uploading…" → "Reading your
      // document…") would never be heard. The live region says it instead.
      webProps={{ style: ROOT_WEB_STYLE }}>
      {dotted ? <BottomSheetDottedBackground /> : null}
      <View
        className="items-center"
        nativeProps={{ style: COLUMN_STYLE }}
        webProps={{ style: COLUMN_WEB_STYLE }}>
        {/* A definite box: Ka's canvas overflows it when he peeks, so
            nothing around him may clip it. */}
        <View
          className="items-center justify-center"
          nativeProps={{ style: KA_STYLE }}
          webProps={{ style: KA_STYLE }}>
          {renderKa(L.kaSize, kaStage)}
        </View>
        <View
          className="w-full items-center"
          webProps={{ role: 'status', 'aria-live': 'polite', style: TITLE_STYLE }}
          nativeProps={{ accessibilityLiveRegion: 'polite', style: TITLE_STYLE }}>
          <Text className="text-center text-h3 font-semibold text-secondary-800 native:font-mona-semibold">
            {title}
          </Text>
          {detail ? (
            <Text className="text-center text-b2 font-medium text-body-600 native:font-mona-medium">
              {detail}
            </Text>
          ) : null}
        </View>
        {progress != null || indeterminate ? (
          // `live`: never moves backwards, smooths sparse updates, a slow
          // sheen while it works so a stall still reads as alive.
          <ProgressBar
            colour="primary"
            live
            indeterminate={progress == null}
            value={progress ?? 0}
            className="w-full"
            accessibilityLabel={progressAccessibilityLabel ?? title}
          />
        ) : null}
        {proTip && !detail ? (
          <ProcessingProTip label={proTip.label} tips={proTip.tips} />
        ) : null}
        {actions ? (
          <View className="flex-row flex-wrap items-center justify-center gap-3">
            {actions}
          </View>
        ) : null}
      </View>
    </View>
  );
}
(ProcessingStage as { displayName?: string }).displayName = 'ProcessingStage';
